'use client';

/**
 * UploadRecovery
 *
 * Mounted at the app root level. On first mount, checks IndexedDB for
 * orphaned recording chunks (from crashed/closed sessions). If viable
 * data is found, automatically re-initiates the upload and drives the
 * UploadOverlay to show progress.
 *
 * Does not require recording-meta — matterId and mimeType are read
 * directly from the chunk records. uploadRecording() fetches a fresh
 * presigned URL via regenerateUploadUrlAction when meta is absent.
 *
 * Runs once on mount — does not poll or re-check.
 */

import { useEffect, useRef, useEffectEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { recordingKeys } from '@/lib/query-keys';
import {
  useRecordingUploadActions,
  useRecordingUploadStore
} from '@/lib/stores/recording-upload-store';
import {
  getOrphanedChunks,
  cleanupExpiredChunks,
  getRecordingDetailAction,
  deleteChunksForRecording,
  deleteRecordingMeta
} from '@/lib/services/recordings';
import { uploadRecording } from './upload-recording';

const log = (step: string, detail?: unknown) =>
  console.log(`[AI Scribe Upload Recovery] ${step}`, detail ?? '');

export function UploadRecovery() {
  const queryClient = useQueryClient();
  const uploadActions = useRecordingUploadActions();
  const hasRunRef = useRef(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runRecovery = useEffectEvent(() => {
    async function recoverOrphans() {
      try {
        await cleanupExpiredChunks();

        const orphans = await getOrphanedChunks();
        if (orphans.size === 0) return;

        // Only recover the first orphan (overlay handles one at a time)
        const [recordingId, chunks] = Array.from(orphans.entries())[0];
        if (chunks.length === 0) return;

        const { matterId } = chunks[0];

        // Skip if an upload is already in progress
        const currentState = useRecordingUploadStore.getState();
        if (currentState.isUploading) return;
        log(
          '1',
          `Recording chunk found for matter (${matterId}) recording (${recordingId})`
        );

        // Check backend status before deciding what to do with the local audio.
        const detail = await getRecordingDetailAction(recordingId);

        // Indeterminate answer (offline, 404, auth blip): never destroy local
        // audio on a fetch we can't trust — leave the chunks for a later
        // recovery pass once the backend is reachable again (LF-3977).
        if (!detail.success) {
          log(
            '2',
            `Recording (${recordingId}) detail unavailable, keeping chunks for a later pass`
          );
          return;
        }

        // Only `uploading` (matter flow — the row is created in `uploading` at
        // record-start) and `draft` (top-level/inline flow — the row stays
        // `draft` for the whole inline capture, never flipping to `uploading`)
        // still need their audio re-uploaded. Any other status means the
        // backend already has — or has rejected — the file, so the leftover
        // local chunks are safe to clear (LF-3977).
        const status = detail.data?.status;
        // `started` (capture initiated, not yet uploaded — LF-4841) joins
        // `uploading`/`draft` as a pre-upload state whose local chunks must be
        // kept for re-upload, not cleaned up.
        if (
          status !== 'uploading' &&
          status !== 'draft' &&
          status !== 'started'
        ) {
          log(
            '2',
            `Recording (${recordingId}) in terminal state (${status ?? 'unknown'}), cleaning up`
          );
          await deleteChunksForRecording(recordingId).catch(() => {});
          await deleteRecordingMeta(recordingId).catch(() => {});
          return;
        }

        // Start the upload overlay in uploading state
        log('2', `Initiating upload`);
        uploadActions.startUpload(
          matterId,
          recordingId,
          'Recovered recording',
          0
        );

        const result = await uploadRecording(recordingId, (progress) => {
          if (progress.total > 0) {
            uploadActions.startUpload(
              matterId,
              recordingId,
              'Recovered recording',
              progress.total
            );
          }
          uploadActions.updateProgress(progress.loaded);
        });

        if (result.success) {
          uploadActions.complete();
          // Orphans carry `matterId 0`, so a matter-scoped key won't refresh
          // the firm-wide `/scribe` list. Invalidate the parent `lists()` key
          // (the ancestor of both `list(matterId)` and the `/scribe`
          // `scribeList`) so every recording list reflects the recovery.
          queryClient.invalidateQueries({
            queryKey: recordingKeys.lists()
          });
          resetTimerRef.current = setTimeout(() => uploadActions.reset(), 5000);
        } else {
          uploadActions.setFailed();
          console.error('[AI Scribe] Recovery upload failed:', result.error);
        }
      } catch {
        // Recovery is best-effort — don't crash the app
      }
    }

    recoverOrphans();
  });
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    runRecovery();
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  return null;
}
